/**
 * CONSENT_GROUPS registry — display metadata for the 9 per-tool consent groups.
 *
 * These are purely UI/display identifiers. They MUST NOT be imported by
 * authorization code (INV-MCP-08). Keys are group IDs, not scope strings.
 */
export const CONSENT_GROUPS = {
    connection_basics: {
        label: 'Connection Essentials',
        description: 'Core identity tools required for the connection to function.',
        audience: 'all',
        hasMutatingTools: false,
        tools: ['whoami'],
    },
    tenant_account: {
        label: 'Account & Activity',
        description: 'View dashboard, balances, and action items',
        audience: 'tenant',
        hasMutatingTools: false,
        tools: ['get_my_dashboard', 'get_action_items'],
    },
    tenant_lease: {
        label: 'Lease & Property Rules',
        description: 'Read lease, documents, and property rules',
        audience: 'tenant',
        hasMutatingTools: false,
        tools: ['get_my_lease', 'list_property_rules'],
    },
    tenant_payments: {
        label: 'Payment History',
        description: 'View payment history and upcoming charges',
        audience: 'tenant',
        hasMutatingTools: false,
        tools: ['get_payment_history'],
    },
    landlord_overview: {
        label: 'Portfolio Overview',
        description: 'Portfolio summary and pending tasks',
        audience: 'manager',
        hasMutatingTools: false,
        tools: ['get_landlord_overview', 'list_pending_tasks'],
    },
    landlord_portfolio: {
        label: 'Properties & Units',
        description: 'Portfolios, properties, units, and occupancy',
        audience: 'manager',
        hasMutatingTools: false,
        tools: [
            'list_portfolios',
            'list_properties',
            'list_units',
            'get_unit_details',
            'get_unit_marketing_profile',
            'get_listing_terms',
        ],
    },
    landlord_financials: {
        label: 'Financial Overview',
        description: 'Rent collection, overdue tenants, and lease financials',
        audience: 'manager',
        hasMutatingTools: false,
        tools: ['get_portfolio_financials', 'list_overdue_tenants', 'get_lease_financials'],
    },
    landlord_tenants: {
        label: 'Tenant Information',
        description: 'Tenant details and lease assignments',
        audience: 'manager',
        hasMutatingTools: false,
        tools: ['list_tenants', 'get_tenant_details'],
    },
    landlord_expenses: {
        label: 'Expenses',
        description: 'Expense records and summaries',
        audience: 'manager',
        hasMutatingTools: false,
        tools: ['list_expenses', 'get_expense_summary'],
    },
    landlord_actions: {
        label: 'Landlord Actions',
        description: 'Match bank transactions and approve late fees',
        audience: 'manager',
        hasMutatingTools: true,
        tools: ['approve_pending_late_fee', 'match_bank_transaction'],
    },
};
/**
 * Derived map from tool name to consent group key.
 * Built programmatically from CONSENT_GROUPS so adding a tool to a group
 * automatically updates this lookup without duplication.
 *
 * Usage in ToolPermissionsPage:
 *   import { TOOL_TO_GROUP } from '@resida/consent-ui'
 *   const group = TOOL_TO_GROUP[toolName] ?? 'other'
 */
export const TOOL_TO_GROUP = Object.entries(CONSENT_GROUPS).reduce((acc, [groupKey, group]) => {
    for (const toolName of group.tools ?? []) {
        acc[toolName] = groupKey;
    }
    return acc;
}, {});
/**
 * Whether a tool is considered mutating based on its group membership.
 * Returns false for tools with no group (safe default).
 */
export function isToolMutating(toolName) {
    const groupKey = TOOL_TO_GROUP[toolName];
    if (!groupKey)
        return false;
    return CONSENT_GROUPS[groupKey]?.hasMutatingTools ?? false;
}
//# sourceMappingURL=consent-groups.js.map